/**
 * Provider-agnostic browser softphone contract. The dashboard and the extension both
 * consume the same `Softphone` UI + `useSoftphone` hook; only the concrete client
 * (`TelnyxSoftphoneClient` today) and the injected mic strategy differ per surface.
 */

export type SoftphoneStatus = 'idle' | 'connecting' | 'ringing' | 'active' | 'ended' | 'error';

/** The carriers the browser softphone can speak to. Mirrors the server's VoiceProviderName. */
export type SoftphoneProvider = 'telnyx' | 'twilio';

/**
 * SIP custom header that carries our `voice_call.id` on the outbound call so the server
 * webhook can correlate back to the row. MUST match the server constant in
 * `packages/server/src/api/services/voice/voiceProvider.types.ts`.
 */
export const VOICE_CALL_ID_HEADER = 'X-Voice-Call-Id';

export interface SoftphoneCallHandlers {
  onStatus: (status: SoftphoneStatus) => void;
  onError: (message: string) => void;
}

export interface SoftphoneDialParams {
  destinationNumber: string;
  callerNumber: string | null;
  voiceCallId: string;
}

/**
 * Platform mic capability — the injectable seam. The dashboard leaves this undefined and
 * lets the SDK call `getUserMedia` directly; the extension supplies a stream captured in
 * its offscreen document (where a persistent mic is allowed under MV3).
 */
export interface SoftphoneMediaStrategy {
  getInputStream?: () => Promise<MediaStream>;
}

export interface SoftphoneClient {
  /** Register lifecycle handlers. Call before `init`/`dial`. */
  on(handlers: SoftphoneCallHandlers): void;
  /** Connect the signaling client with a freshly minted token. */
  init(token: string): Promise<void>;
  /** Place the outbound call. */
  dial(params: SoftphoneDialParams): Promise<void>;
  hangup(): void;
  setMuted(muted: boolean): void;
  /**
   * Send DTMF tones on the live call (IVR navigation). Accepts `0-9`, `*`, `#`.
   * No-op when no call is active (parity with hangup/setMuted). May throw on providers that
   * reject digits before media is established — callers guard with try/catch.
   */
  sendDigits(digits: string): void;
  /**
   * End the current call and release its per-call resources (disconnect, detach the call's
   * listeners, clear the active call) WITHOUT tearing down the signaling client/device — so the
   * next dial reuses the already-registered connection. Idempotent; safe with no active call.
   */
  closeCall(): void;
  /** Tear down the call, signaling connection, and any DOM/audio resources. */
  destroy(): Promise<void>;
}
